import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { authFileClick } from "@/lib/openFile";
import Layout from "@/components/Layout";
import StatusChip from "@/components/StatusChip";
import OrderTimeline from "@/components/OrderTimeline";
import AuthImage from "@/components/AuthImage";
import { useCategories } from "@/hooks/useCategories";
import { ArrowLeft, FileText, ImageSquare, ShieldCheck, Calendar, Ruler, Certificate, DownloadSimple, Package } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate, formatCurrency } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const BYTES_PER_KB = 1024;

export default function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { byId } = useCategories();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setOrder(res.data);
    } catch (e) {
      console.error("[OrderDetail] fetchOrder failed:", e);
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  if (loading) {
    return (
      <Layout>
        <div className="h-96 bg-white border border-zinc-200 animate-pulse" />
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <div className="text-zinc-500">Order not found</div>
          <button onClick={() => navigate(-1)} className="mt-4 underline">Go back</button>
        </div>
      </Layout>
    );
  }

  const files = order.files || [];
  const testCerts = files.filter((f) => f.kind === "test_certificate");
  const warranties = files.filter((f) => f.kind === "warranty");
  const photos = files.filter((f) => f.kind === "photo");
  const isAdmin = user?.role === "admin";

  return (
    <Layout>
      <Link
        to={isAdmin ? "/admin" : "/dashboard"}
        data-testid="back-link"
        className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
      >
        <ArrowLeft size={14} /> Back to orders
      </Link>

      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
        <div className="lg:col-span-7">
          {(() => {
            const cat = byId(order.category_id);
            const blob = cat?.blob || "bg-zinc-100";
            const accent = cat?.accent_fg || "text-zinc-400";
            return (
              <div className={`aspect-[16/10] ${blob} border border-zinc-200 overflow-hidden relative grid place-items-center`}>
                {order.product_image_path ? (
                  <AuthImage
                    path={order.product_image_path}
                    alt={order.product_name}
                    className="absolute inset-0 w-full h-full object-cover"
                    fallback={<Package size={72} weight="duotone" className={accent} />}
                  />
                ) : order.hero_image_url ? (
                  <img
                    src={order.hero_image_url}
                    alt={order.product_name}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="product-hero-image"
                  />
                ) : (
                  <Package size={72} weight="duotone" className={accent} />
                )}
              </div>
            );
          })()}
        </div>
        <div className="lg:col-span-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="mt-tiny text-zinc-400 font-mono-mt">{order.order_number}</div>
              <StatusChip status={order.current_status} testId="detail-status-chip" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black text-zinc-900 mt-3 tracking-tight leading-[1.05]">
              {order.product_name}
            </h1>
            <div className="mt-2 text-zinc-500">{order.product_type}</div>

            <div className="grid grid-cols-2 gap-4 mt-8">
              <MetaCell label="Quantity" value={order.quantity} />
              <MetaCell label="Unit Price" value={formatCurrency(order.unit_price)} />
              <MetaCell label="Total" value={formatCurrency(order.total_price)} accent />
              <MetaCell label="Expected Delivery" value={formatDate(order.expected_delivery)} />
              <MetaCell label="Manufactured" value={formatDate(order.manufacturing_date)} />
              <MetaCell label="Client" value={order.client_name} />
            </div>
          </div>
          {isAdmin && (
            <Link
              to={`/admin/orders/${order.order_id}`}
              data-testid="admin-manage-button"
              className="mt-8 inline-flex items-center justify-center bg-[#FF5A1F] hover:bg-[#E04814] text-white px-6 py-3 font-medium transition-colors duration-200"
            >
              Manage Order
            </Link>
          )}
        </div>
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="timeline" className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-zinc-200 w-full justify-start h-auto p-0 gap-0">
          <TabsTrigger
            value="timeline"
            data-testid="tab-timeline"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium"
          >
            Timeline
          </TabsTrigger>
          <TabsTrigger
            value="specs"
            data-testid="tab-specs"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium"
          >
            Specifications
          </TabsTrigger>
          <TabsTrigger
            value="docs"
            data-testid="tab-docs"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium"
          >
            Documents
          </TabsTrigger>
          <TabsTrigger
            value="warranty"
            data-testid="tab-warranty"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium"
          >
            Warranty
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-8">
          <div className="bg-white border border-zinc-200 p-6 md:p-10">
            <div className="mt-tiny text-zinc-400 mb-6">Manufacturing Journey</div>
            <OrderTimeline timeline={order.timeline || []} current_status={order.current_status} />
          </div>
        </TabsContent>

        <TabsContent value="specs" className="mt-8">
          <div className="bg-white border border-zinc-200">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
              {Object.entries(order.specifications || {}).map(([k, v], i) => (
                <div key={k} className={`p-6 border-b border-zinc-200 ${i % 2 === 0 ? "" : ""}`}>
                  <div className="mt-tiny text-zinc-400 flex items-center gap-2"><Ruler size={12} /> {k}</div>
                  <div className="mt-2 font-mono-mt text-zinc-900">{String(v)}</div>
                </div>
              ))}
            </div>
            {Object.keys(order.specifications || {}).length === 0 && (
              <div className="p-8 text-zinc-500 text-center">No specifications provided</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="docs" className="mt-8">
          <div className="grid gap-6">
            <DocumentSection icon={Certificate} title="Quality Test Certificates" files={files.filter((f) => f.kind === "test_certificate")} emptyLabel="No test certificates uploaded yet" />
            <DocumentSection icon={FileText} title="Drawings & Dimensions" files={files.filter((f) => f.kind === "drawing")} emptyLabel="No drawings uploaded yet" />
            <DocumentSection icon={FileText} title="Specification Sheet" files={files.filter((f) => f.kind === "spec_sheet")} emptyLabel="No specifications uploaded yet" />
            <DocumentSection icon={FileText} title="Technical Data Sheet" files={files.filter((f) => f.kind === "tech_datasheet")} emptyLabel="No technical data sheet uploaded yet" />
            <DocumentSection icon={ImageSquare} title="Production Photos" files={files.filter((f) => f.kind === "photo")} emptyLabel="No photos uploaded yet" isImage />
          </div>
        </TabsContent>

        <TabsContent value="warranty" className="mt-8">
          <div className="bg-white border border-zinc-200 p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#0047AB] grid place-items-center">
                <ShieldCheck size={24} weight="fill" className="text-white" />
              </div>
              <div className="flex-1">
                <div className="font-display text-xl font-bold text-zinc-900">Warranty Information</div>
                <p className="mt-3 text-zinc-700 leading-relaxed">{order.warranty_info || "Standard manufacturer warranty applies."}</p>
                {order.manufacturing_date && (
                  <div className="mt-6 inline-flex items-center gap-2 mt-tiny text-zinc-500 border-l-2 border-[#FF5A1F] pl-3">
                    <Calendar size={14} /> Warranty starts {formatDate(order.manufacturing_date)}
                  </div>
                )}
              </div>
            </div>
            {warranties.length > 0 && (
              <div className="mt-8 pt-8 border-t border-zinc-200">
                <div className="mt-tiny text-zinc-400 mb-4">Warranty Documents</div>
                <div className="grid gap-2">
                  {warranties.map((f) => <FileRow key={f.file_id} file={f} />)}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function MetaCell({ label, value, accent }) {
  return (
    <div className={`${accent ? "col-span-2 bg-zinc-900 text-white p-4" : "p-4 bg-white border border-zinc-200"}`}>
      <div className={`mt-tiny ${accent ? "text-white/60" : "text-zinc-400"}`}>{label}</div>
      <div className={`font-display font-bold mt-1 ${accent ? "text-2xl" : "text-lg text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function DocumentSection({ icon: Icon, title, files, emptyLabel, isImage }) {
  return (
    <div className="bg-white border border-zinc-200">
      <div className="p-6 border-b border-zinc-200 flex items-center gap-3">
        <Icon size={20} className="text-[#0047AB]" weight="duotone" />
        <div className="font-display font-bold text-zinc-900">{title}</div>
      </div>
      <div className="p-6">
        {files.length === 0 ? (
          <div className="text-zinc-400 text-sm">{emptyLabel}</div>
        ) : isImage ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {files.map((f) => <PhotoTile key={f.file_id} file={f} />)}
          </div>
        ) : (
          <div className="grid gap-2">
            {files.map((f) => <FileRow key={f.file_id} file={f} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FileRow({ file }) {
  const url = `${API}/files/${file.storage_path}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={authFileClick(file.storage_path)}
      data-testid={`file-${file.file_id}`}
      className="flex items-center justify-between gap-4 p-4 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 transition-colors group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <FileText size={20} className="text-[#0047AB] shrink-0" weight="duotone" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-900 truncate">{file.original_filename}</div>
          <div className="mt-tiny text-zinc-400">{(file.size / BYTES_PER_KB).toFixed(1)} KB</div>
        </div>
      </div>
      <DownloadSimple size={18} className="text-zinc-400 group-hover:text-[#FF5A1F]" />
    </a>
  );
}

function PhotoTile({ file }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const res = await api.get(`/files/${file.storage_path}`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        setSrc(url);
        revoke = url;
      } catch (e) {
        console.error("[PhotoTile] load failed:", e);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [file.storage_path]);
  return (
    <div className="aspect-square bg-zinc-100 border border-zinc-200 overflow-hidden">
      {src ? <img src={src} alt={file.original_filename} className="w-full h-full object-cover" /> : null}
    </div>
  );
}
